import React, { useEffect, useState } from 'react';
import {
  Create,
  SimpleForm,
  TextInput,
  ReferenceInput,
  SelectInput,
  required,
  useGetList,
  useCreate,
  useRedirect,
  useNotify,
} from 'react-admin';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  Divider,
} from '@mui/material';
import {
  AccountTree as DiagramIcon,
  Factory as PlantIcon,
  Group as GroupIcon,
} from '@mui/icons-material';
import RolePermissionManager from '../../components/RolePermissionManager';
import { useRolePermissionTransform } from '../../hooks/useRolePermissionTransform';

const DiagramsCreate = () => {
  // Estado para almacenar el parámetro preseleccionado
  const [preselectedPlantId, setPreselectedPlantId] = useState<string | null>(
    null
  );

  const { transformRoles } = useRolePermissionTransform();

  // Obtener parámetros de la URL usando window.location de forma segura
  useEffect(() => {
    // Para hash routing, necesitamos obtener los parámetros del hash
    const hash = window.location.hash;
    const queryIndex = hash.indexOf('?');
    if (queryIndex !== -1) {
      const queryString = hash.substring(queryIndex + 1);
      const urlParams = new URLSearchParams(queryString);
      const plantId = urlParams.get('plantId');
      setPreselectedPlantId(plantId);
    }
  }, []);

  console.log('PARAMS', preselectedPlantId);

  // Obtener lista de plantas
  const { data: plants, isLoading: plantsLoading } = useGetList('plants', {
    pagination: { page: 1, perPage: 100 },
    sort: { field: 'plantName', order: 'ASC' },
  });

  const plantChoices = plants
    ? plants.map((plant: any) => ({
        id: plant.id,
        name: plant.plantName,
      }))
    : [];

  // Hooks para manejar la creación personalizada
  const [create] = useCreate();
  const redirect = useRedirect();
  const notify = useNotify();

  // Función personalizada para manejar el envío del formulario
  const handleSave = async (data: any) => {
    try {
      // Asegurar que plantId esté incluido si viene preseleccionado
      const payload = {
        ...data,
        ...(preselectedPlantId && {
          plantId: parseInt(preselectedPlantId),
        }),
      };

      console.log('Payload enviado:', payload);

      await create('diagrams', { data: payload });

      notify('Diagrama creado exitosamente', { type: 'success' });

      // Redirección personalizada
      const redirectTo = preselectedPlantId
        ? `/plants/${preselectedPlantId}/show`
        : '/diagrams';
      redirect(redirectTo);
    } catch (error) {
      console.error('Error creando diagrama:', error);
      notify('Error al crear el diagrama', { type: 'error' });
    }
  };

  return (
    <Create
      title='Crear Diagrama'
      redirect={false} // Deshabilitamos el redirect automático
      transform={transformRoles}
    >
      <Box sx={{ padding: 3, maxWidth: 1000, margin: '0 auto' }}>
        <Card sx={{ boxShadow: 3, borderRadius: 2 }}>
          <CardContent>
            <Box display='flex' alignItems='center' mb={3}>
              <DiagramIcon
                sx={{ mr: 2, fontSize: 32, color: 'primary.main' }}
              />
              <Box>
                <Typography variant='h5' component='h1' fontWeight='bold'>
                  Nuevo Diagrama
                </Typography>
                {preselectedPlantId && (
                  <Typography variant='body2' color='text.secondary'>
                    Se creará en la planta seleccionada (planta bloqueada)
                  </Typography>
                )}
              </Box>
            </Box>

            <SimpleForm
              onSubmit={handleSave} // Usar nuestro handler personalizado
              // Establecer valores por defecto si se pasa un plantId
              defaultValues={
                preselectedPlantId
                  ? { plantId: parseInt(preselectedPlantId) }
                  : undefined
              }
              sx={{
                '& .RaSimpleForm-toolbar': {
                  marginTop: 3,
                  padding: 2,
                  backgroundColor: 'background.paper',
                  borderRadius: 2,
                  boxShadow: 2,
                },
              }}
            >
              <Grid container spacing={3}>
                {/* Información Principal */}
                <Grid item xs={12} md={6}>
                  <Card
                    sx={{
                      height: 'fit-content',
                      boxShadow: 2,
                      borderRadius: 2,
                      backgroundColor: 'background.default',
                    }}
                  >
                    <CardContent>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          mb: 2,
                        }}
                      >
                        <DiagramIcon color='primary' />
                        <Typography variant='h6' fontWeight='bold'>
                          Información del Diagrama
                        </Typography>
                      </Box>
                      <Divider sx={{ mb: 2 }} />

                      <TextInput
                        source='name'
                        label='Nombre del Diagrama'
                        validate={[required()]}
                        fullWidth
                        sx={{ mb: 2 }}
                        helperText='Nombre descriptivo para el diagrama'
                      />

                      <TextInput
                        source='description'
                        label='Descripción'
                        fullWidth
                        multiline
                        rows={3}
                        sx={{ mb: 2 }}
                        helperText='Descripción opcional del diagrama (opcional)'
                      />
                    </CardContent>
                  </Card>
                </Grid>

                {/* Planta y Configuración */}
                <Grid item xs={12} md={6}>
                  <Card
                    sx={{
                      height: 'fit-content',
                      boxShadow: 2,
                      borderRadius: 2,
                      backgroundColor: 'background.default',
                    }}
                  >
                    <CardContent>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          mb: 2,
                        }}
                      >
                        <PlantIcon color='primary' />
                        <Typography variant='h6' fontWeight='bold'>
                          Asignación y Permisos
                        </Typography>
                      </Box>
                      <Divider sx={{ mb: 2 }} />

                      <ReferenceInput
                        source='plantId'
                        reference='plants'
                        sort={{ field: 'plantName', order: 'ASC' }}
                      >
                        <SelectInput
                          optionText='plantName'
                          optionValue='id'
                          label='Planta'
                          validate={[required()]}
                          fullWidth
                          isLoading={plantsLoading}
                          disabled={!!preselectedPlantId} // Deshabilitar si viene preseleccionado
                          helperText={
                            preselectedPlantId
                              ? 'Planta preseleccionada desde la navegación'
                              : 'Selecciona la planta a la que pertenece este diagrama'
                          }
                        />
                      </ReferenceInput>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Gestión de Roles */}
                <Grid item xs={12}>
                  <Card
                    sx={{
                      boxShadow: 2,
                      borderRadius: 2,
                      backgroundColor: 'background.default',
                    }}
                  >
                    <CardContent>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          mb: 2,
                        }}
                      >
                        <GroupIcon color='primary' />
                        <Typography variant='h6' fontWeight='bold'>
                          Roles de Acceso
                        </Typography>
                      </Box>
                      <Divider sx={{ mb: 2 }} />
                      <RolePermissionManager
                        source="roles"
                        title="Gestión de Roles para Diagrama"
                        subtitle="Configura los roles que tendrán acceso a este diagrama y su nivel de permisos"
                        helperText="Selecciona roles y asigna niveles de permisos (READ, WRITE, ADMIN) para controlar el acceso a este diagrama"
                      />
                      <Box sx={{ mt: 2 }}>
                        <Typography variant='body2' color='text.secondary'>
                          Los roles determinan qué usuarios pueden ver y
                          gestionar este diagrama. Puedes agregar múltiples
                          roles según sea necesario.
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </SimpleForm>
          </CardContent>
        </Card>
      </Box>
    </Create>
  );
};

export default DiagramsCreate;
